import { IAccumulatedStats, IBodyScanData, IInterval, ITeamChallenge, TimeSpan } from 'fitbeat.models';
import { UserProfile, UserWorkoutSession } from '../../entities';
import { IBodyScansRepository, IUserProfileRepository, IWorkoutRepository } from '../../persistence';
import { ITeamChallenges } from './iteamchallenges';

export class TeamChallenges implements ITeamChallenges {
  private static formatInterval(interval: IInterval) {
    return {
      oneweek: interval.oneweek === undefined ? undefined : interval.oneweek.toFixed(2),
      fourweek: interval.fourweek.toFixed(2),
      always: interval.always.toFixed(2),
    };
  }

  constructor(
    private userRepo: IUserProfileRepository,
    private workoutRepo: IWorkoutRepository,
    private bodyScanRepo: IBodyScansRepository) { }

  public async getTeamChallengeStats(teamChallenge: ITeamChallenge): Promise<any> {

    const allEmailsInChallenge = [].concat.apply([], teamChallenge.teams.map((team) => [...team.emails]));
    const users =  await this.getSignedUpUsersID(allEmailsInChallenge);
    const startDate = new Date(teamChallenge.startDate);
    const endDate = new Date(teamChallenge.endDate);

    const stats = await teamChallenge.teams.map(async (team) => {
      const teamEmails = team.emails;
      const teamUsers = users.filter((user) => team.emails.indexOf(user.email) >= 0);
      const existingTeamIds = teamUsers.map((user) => user.uid);
      const workoutsForTeam = await this.getWorkoutsForTeam(existingTeamIds, startDate, endDate);
      const teamSortedBodyScans = await this.getSortedBodyScansForTeam(team.emails, startDate, endDate);

      const avgAttendance: IInterval = this.calculateAttendance(workoutsForTeam, teamEmails.length, endDate);
      const avgBodyFatChange: IInterval = this.calculateAvgBodyFatChange(teamEmails, teamSortedBodyScans);
      const avgLeanMuscleChange: IInterval = this.calculateAvgLeanMuscleChange(teamEmails, teamSortedBodyScans);
      const maxCalsPerWorkout: number = this.getMaxCalories(workoutsForTeam);
      const maxPointsPerWorkout: number = this.getMaxEffortPoints(workoutsForTeam);
      const maxAttendance: number = this.getMaxAttendance(workoutsForTeam);

      return {
        name: team.name,
        avgAttendance: TeamChallenges.formatInterval(avgAttendance),
        avgBodyFatChange: TeamChallenges.formatInterval(avgBodyFatChange),
        avgLeanMuscleChange: TeamChallenges.formatInterval(avgLeanMuscleChange),
        maxCalsPerWorkout,
        maxPointsPerWorkout,
        maxAttendance,
      };
    });

    const teamStats = await Promise.all(stats);
    const individualStats: IAccumulatedStats = {
      maxAttendance: Math.max(...teamStats.map((s) => s.maxAttendance), 0),
      maxCalsPerWorkout: Math.max(...teamStats.map((s) => s.maxCalsPerWorkout), 0),
      maxPointsPerWorkout: Math.max(...teamStats.map((s) => s.maxPointsPerWorkout), 0),
    };

    return {
      signups: users.map((user) => user.email),
      individualStats,
      teamStats,
      teamChallenge,
    };
  }

  private async getSignedUpUsersID(emails: string[]): Promise<UserProfile[]> {
    return await this.userRepo.getUsersByEmails(emails);
  }

  private calculateAttendance(workouts: UserWorkoutSession[], totalTeamMembers: number, endDate: Date): IInterval {
    const oneWeekAgo = TimeSpan.getXDaysAgo(7, endDate);
    const oneWeekWorkouts = workouts.filter((workout) =>
      new Date(workout.endTime) >= oneWeekAgo && new Date(workout.endTime) <= endDate);

    const fourWeeksAgo = TimeSpan.getXDaysAgo(28, endDate);
    const fourWeekWorkouts = workouts.filter((workout) =>
      new Date(workout.endTime) >= fourWeeksAgo && new Date(workout.endTime) <= endDate);

    return {
      oneweek: oneWeekWorkouts.length / totalTeamMembers || 0,
      fourweek: fourWeekWorkouts.length / totalTeamMembers || 0,
      always: workouts.length / totalTeamMembers || 0,
    };
  }

  private calculateAvgBodyFatChange(memberEmails: string[], bodyScans: IBodyScanData[]): IInterval {
    const average = memberEmails.reduce((result: IInterval, email) => {
      const scansForMember = bodyScans.filter((scan) => scan.email === email);
      const scansForMemberFourWeeks = this.getScansWithinFourWeeks(scansForMember);

      result.always += this.getFirstAndLastAnalysisDifference(scansForMember, 'percentBodyFat');
      result.fourweek += this.getFirstAndLastAnalysisDifference(scansForMemberFourWeeks, 'percentBodyFat');
      return result;
    }, { fourweek: 0, always: 0 });

    return {
      fourweek: average.fourweek / memberEmails.length || 0,
      always: average.always / memberEmails.length || 0,
    };
  }

  private calculateAvgLeanMuscleChange(memberEmails: string[], bodyScans: IBodyScanData[]): IInterval {
    const average = memberEmails.reduce((result: IInterval, email) => {
      const scansForMember = bodyScans.filter((scan) => scan.email === email);
      const scansForMemberFourWeeks = this.getScansWithinFourWeeks(scansForMember);

      result.always += this.getFirstAndLastAnalysisDifference(scansForMember, 'skeletalMuscleMass');
      result.fourweek += this.getFirstAndLastAnalysisDifference(scansForMemberFourWeeks, 'skeletalMuscleMass');
      return result;
    }, { fourweek: 0, always: 0 });

    return {
      fourweek: Math.round(average.fourweek / memberEmails.length) || 0,
      always: Math.round(average.always / memberEmails.length) || 0,
    };
  }

  private async getWorkoutsForTeam(teamIds: string[], from: Date, to: Date): Promise<UserWorkoutSession[]> {
    return await this.workoutRepo.getAllWorkoutsForUsers(teamIds, from, to);
  }

  private getMaxCalories(workouts: UserWorkoutSession[]): number {
    const result = workouts.map((workout) => {
      if (workout.performanceMetrics && workout.performanceMetrics.calories) {
        return workout.performanceMetrics.calories;
      }
      return 0;
    });

    return Math.max(...result, 0);
  }

  private getMaxEffortPoints(workouts: UserWorkoutSession[]): number {
    const result = workouts.map((workout) => {
      if (workout.performanceMetrics && workout.performanceMetrics.load) {
        return workout.performanceMetrics.load;
      }
      return 0;
    });

    return Math.max(...result, 0);
  }

  private async getSortedBodyScansForTeam(emails: string[], from: Date, to: Date): Promise<IBodyScanData[]> {
    const bodyScans = await this.bodyScanRepo.getForUsers(emails, from, to);
    return bodyScans.sort((a: IBodyScanData, b: IBodyScanData) =>
      new Date(b.dateOfScan).getTime() - new Date(a.dateOfScan).getTime());
  }

  private getFirstAndLastAnalysisDifference(bodyScans: IBodyScanData[], metricAnalysis: string): number {
    if (bodyScans.length < 1) {
      return 0;
    }

    const first = bodyScans[0].analysis;
    const last = bodyScans[bodyScans.length - 1].analysis;

    if (first && first[metricAnalysis] && last && last[metricAnalysis]) {
      return (first[metricAnalysis].currentValue - last[metricAnalysis].currentValue);
    }

    return 0;
  }

  private getScansWithinFourWeeks(bodyScans: IBodyScanData[]): IBodyScanData[] {
    const fourWeeksAgo = TimeSpan.getXDaysAgo(28, new Date());
    const result = bodyScans.filter((scan) =>
      new Date(scan.dateOfScan) >= fourWeeksAgo && new Date(scan.dateOfScan) <= new Date());

    // Append last body scan before 4 week period
    if (result.length === 1 && bodyScans.length > 1) {
      result.push(bodyScans[result.length]);
    }

    return result;
  }

  private getMaxAttendance(workouts: UserWorkoutSession[]): number {
    let highestOccurance = 0;
    const counts = {};

    workouts.forEach(({ userProfileId }) => {
      counts[userProfileId] = counts[userProfileId] ? counts[userProfileId] + 1 : 1;
      if (counts[userProfileId] > highestOccurance) {
        highestOccurance = counts[userProfileId];
      }
    });

    return highestOccurance;
  }
}
